import { AxiosResponse } from "axios";
import { AnimatePresence } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ErrorModal from "../components/Modals/ErrorModal";
import StyledSelect from "../components/StyledInputs/StyledSelect";
import { FixedServiceRequestTypes } from "../core/constants/constants";
import { apiService } from "../core/services/apiService";
import { IHome, IServiceRequestForAPI } from "../core/types/types";
import IllegalDumpingPath from "../paths/IllegalDumpingPath/IllegalDumpingPath";
import LooseLitterPath from "../paths/LooseLitterPath/LooseLitterPath";
import PublicLitterBinsPath from "../paths/PublicLitterBins/PublicLittersBinsPath";
import StandardServicePath from "../paths/StandardServicePath/StandardServicePath";

function Home({
  initialState,
  serviceRequest,
  updateServiceRequest,
  updateHasCompletedForm,
}: IHome) {
  const navigate = useNavigate();

  // state for custom validation errors
  const [formErrorMessage, updateFormErrorMessage] = useState<string | null>(null);
  const [checkboxErrorMessage, updateCheckboxErrorMessage] = useState<string | null>(null);
  const [customerNoError, updateCustomerNoError] = useState<string | null>(null);
  const [assessmentNoError, updateAssessmentNoError] = useState<string | null>(null);
  const [wasteDescError, updateWasteDescError] = useState<string | null>(null);
  const errors = {
    checkboxError: "You need to select a service from the checkboxes above.",
    invalidAssessmentNo: "Please enter a valid assessment number",
    invalidCustomerNo: "Please enter a valid customer number",
    specialCharValidationError: "Please remove all specials characters from this field",
    apiError: "Something went wrong with this request. Please try again later or contact us.",
  };

  // state for manual entry of addresses
  const [manualAddress, updateManualAddress] = useState(false);
  const [backupStreetNo, updateBackupStreetNo] = useState("");
  const [backupStreetName, updateBackupStreetName] = useState("");
  const [backupPostCode, updateBackupPostCode] = useState("");

  // state for waste details when 'other' is selected in multi-select
  const [otherWasteDetails, updateOtherWasteDetails] = useState("");

  // loading state
  const [isLoading, updateIsLoading] = useState(false);

  // Error modal state
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // SUBMIT HANDLER FOR ALL SERVICE REQUEST TYPES
  async function onSubmit(overrideConfirmation: boolean, overrideError: boolean) {
    updateFormErrorMessage(null);
    updateIsLoading(true);
    // validate custom form components
    if (customValidation()) {
      // if confirmation has not been given for service requests that require it, show confirmation modal
      if (
        (serviceRequest.serviceRequest === FixedServiceRequestTypes.ADDITIONAL_BIN ||
          serviceRequest.serviceRequest === FixedServiceRequestTypes.CHANGE_BIN_SIZES ||
          serviceRequest.serviceRequest === FixedServiceRequestTypes.GARDEN_WASTE_NEW ||
          serviceRequest.serviceRequest === FixedServiceRequestTypes.GARDEN_WASTE_CHANGE ||
          serviceRequest.serviceRequest === FixedServiceRequestTypes.CANCEL_GARDEN_WASTE ||
          serviceRequest.serviceRequest === FixedServiceRequestTypes.NEW_PROPERTY_BIN) &&
        !overrideConfirmation
      ) {
        updateIsLoading(false);
        setIsConfirmationModalOpen(true);
      } else {
        let serviceRequestForApi: IServiceRequestForAPI = Object.assign({}, serviceRequest, {
          service: serviceRequest.service === null ? null : serviceRequest.service.toString(),
        });

        if (manualAddress && backupStreetNo && backupStreetName && backupPostCode) {
          //Latitude, longitude and suburb name is cleared to rehydrate address components correctly in API when manual address is used
          serviceRequestForApi.addressComponents.Latitude = null;
          serviceRequestForApi.addressComponents.Longitude = null;
          serviceRequestForApi.addressComponents.SuburbName = "";

          serviceRequestForApi = {
            ...serviceRequestForApi,
            addressComponents: {
              ...serviceRequestForApi.addressComponents,
              StreetNumber: backupStreetNo.trim(),
              RoadName: backupStreetName.trim(),
              PostCode: backupPostCode.trim(),
            },
          };
        }

        if (serviceRequest.wasteDetails.wasteDescription?.includes("Other")) {
          serviceRequestForApi.wasteDetails.wasteDescription =
            serviceRequest.wasteDetails.wasteDescription + `, ${otherWasteDetails}`;
        }

        const response = await apiService.createServiceRequest(serviceRequestForApi, overrideError);
        handleResponse(response, overrideError);
      }
    }
    updateIsLoading(false);
  }

  // RESPONSE HANDLER FOR ALL SUBMIT METHODS
  function handleResponse(response: AxiosResponse, overrideError: boolean) {
    // set ticket number and navigate to confirmation page on success
    if (response?.status === 200 && parseInt(response.data)) {
      serviceRequest.ticketNumber = parseInt(response.data);
      updateHasCompletedForm(true);
      updateIsLoading(false);
      if (overrideError) setIsErrorModalOpen(false);
      navigate("/confirmation");
      // show error modal with API response (this has the option to continue) for 900 errors
    } else if (response?.status === 900) {
      updateIsLoading(false);
      setErrorMessage(response.data);
      setIsErrorModalOpen(true);
      // show error message from API for 999, otherwise show generaic API error
    } else {
      updateIsLoading(false);
      updateFormErrorMessage(response?.status === 999 ? response.data : errors.apiError);
    }
  }

  // CUSTOM FORM VALIDATION FOR ALL SERVICE REQUEST TYPES
  function customValidation(): boolean {
    let isValid = true;
    // validate service checkboxes when they are present
    if (
      (serviceRequest.serviceRequest === FixedServiceRequestTypes.DAMAGED_BIN ||
        serviceRequest.serviceRequest === FixedServiceRequestTypes.MISSING_BIN ||
        serviceRequest.serviceRequest === FixedServiceRequestTypes.ADDITIONAL_BIN ||
        serviceRequest.serviceRequest === FixedServiceRequestTypes.MISSED_COLLECTION) &&
      (serviceRequest.service === null || !serviceRequest.service.length)
    ) {
      isValid = false;
      updateCheckboxErrorMessage(errors.checkboxError);
    }
    if (
      serviceRequest.serviceRequest === FixedServiceRequestTypes.ADDITIONAL_BIN ||
      serviceRequest.serviceRequest === FixedServiceRequestTypes.CHANGE_BIN_SIZES ||
      serviceRequest.serviceRequest === FixedServiceRequestTypes.GARDEN_WASTE_NEW ||
      serviceRequest.serviceRequest === FixedServiceRequestTypes.GARDEN_WASTE_CHANGE ||
      serviceRequest.serviceRequest === FixedServiceRequestTypes.CANCEL_GARDEN_WASTE
    ) {
      if (
        serviceRequest.assessmentNumber != null &&
        RegExp(/[^A-Za-z0-9]/g).test(serviceRequest.assessmentNumber)
      ) {
        isValid = false;
        updateAssessmentNoError(errors.specialCharValidationError);
      }
      if (
        serviceRequest.customerNumber != null &&
        RegExp(/[^A-Za-z0-9]/g).test(serviceRequest.customerNumber)
      ) {
        isValid = false;
        updateCustomerNoError(errors.specialCharValidationError);
      }
      if (
        serviceRequest.assessmentNumber === null ||
        serviceRequest.assessmentNumber.length === 0
      ) {
        isValid = false;
        updateAssessmentNoError(errors.invalidAssessmentNo);
      }
      if (
        serviceRequest.customerNumber === null ||
        serviceRequest.customerNumber.length === 0 ||
        parseInt(serviceRequest.customerNumber) + 1 === 1
      ) {
        isValid = false;
        updateCustomerNoError(errors.invalidCustomerNo);
      }
    }

    if (
      serviceRequest.serviceRequest === FixedServiceRequestTypes.ILLEGAL_DUMPING &&
      (serviceRequest.wasteDetails.wasteDescription === null ||
        serviceRequest.wasteDetails.wasteDescription === "")
    ) {
      updateWasteDescError("You need to select an option above");
      isValid = false;
    }
    return isValid;
  }

  return (
    <div className="mt-10 mb-40 w-11/12 md:w-3/4 xl:7/12">
      <div className="py-12 my-auto mx-auto flex flex-col items-start justify-start xl:w-4/5 2xl:w-7/12">
        <h1
          className={`heading-font w-full text-left ${
            serviceRequest.serviceRequest ===
            FixedServiceRequestTypes.NEW_PROPERTY_BIN
              ? "mb-3"
              : "mb-6"
          }`}
        >
          How can we help you?
        </h1>
        {serviceRequest.serviceRequest ===
          FixedServiceRequestTypes.NEW_PROPERTY_BIN && (
          <p className="text-sm text-white mb-6">
            Please note to be eligible for this service, your residential
            property must have a Code Compliance Certificate (CCC) already
            issued. Please check the full{" "}
            <a
              href="https://cms.environz.co.nz/media/Terms-and-Conditions-Bins-for-new-property.pdf"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Terms & Conditions
            </a>{" "}
            before starting your application.
          </p>
        )}
        {/******** SERVICE REQUEST DROPDOWN ********/}
        <StyledSelect
          label={"Please select your service request"}
          serviceRequestDropdowns={FixedServiceRequestTypes.serviceRequestList}
          defaultValue={serviceRequest.serviceRequest ?? ""}
          onChange={(e) => {
            // when path is change, reset service request
            // and all custom validation & loading states
            updateIsLoading(false);
            updateFormErrorMessage(null);
            updateCheckboxErrorMessage(null);
            updateAssessmentNoError(null);
            updateCustomerNoError(null);
            updateWasteDescError(null);
            updateManualAddress(false);
            updateBackupStreetNo("");
            updateBackupStreetName("");
            updateBackupPostCode("");
            updateServiceRequest({
              ...initialState,
              serviceRequest: (e.target as HTMLInputElement).value,
            });
          }}
        />
        {/******** PATH FOR DAMAGED BIN SERVICE REQUESTS ********/}
        <AnimatePresence>
          {serviceRequest?.serviceRequest === FixedServiceRequestTypes.DAMAGED_BIN && (
            <StandardServicePath
              key={1}
              serviceRequest={serviceRequest}
              updateServiceRequest={updateServiceRequest}
              onSubmit={onSubmit}
              checkboxErrorMessage={checkboxErrorMessage}
              updateCheckboxErrorMessage={updateCheckboxErrorMessage}
              isLoading={isLoading}
              formErrorMessage={formErrorMessage}
              assessmentNoError={assessmentNoError}
              updateAssessmentNoError={updateAssessmentNoError}
              customerNoError={customerNoError}
              updateCustomerNoError={updateCustomerNoError}
              backupStreetNo={backupStreetNo}
              backupStreetName={backupStreetName}
              updateBackupStreetNo={updateBackupStreetNo}
              updateBackupPostCode={updateBackupPostCode}
              updateBackupStreetName={updateBackupStreetName}
              manualAddress={manualAddress}
              updateManualAddress={updateManualAddress}
              isConfirmationModalOpen={isConfirmationModalOpen}
              setIsConfirmationModalOpen={setIsConfirmationModalOpen}
            />
          )}
          {/******** PATH FOR MISSING BIN SERVICE REQUESTS ********/}
          {serviceRequest?.serviceRequest === FixedServiceRequestTypes.MISSING_BIN && (
            <StandardServicePath
              key={2}
              serviceRequest={serviceRequest}
              updateServiceRequest={updateServiceRequest}
              onSubmit={onSubmit}
              checkboxErrorMessage={checkboxErrorMessage}
              updateCheckboxErrorMessage={updateCheckboxErrorMessage}
              isLoading={isLoading}
              formErrorMessage={formErrorMessage}
              assessmentNoError={assessmentNoError}
              updateAssessmentNoError={updateAssessmentNoError}
              customerNoError={customerNoError}
              updateCustomerNoError={updateCustomerNoError}
              backupStreetNo={backupStreetNo}
              backupStreetName={backupStreetName}
              updateBackupStreetNo={updateBackupStreetNo}
              updateBackupPostCode={updateBackupPostCode}
              updateBackupStreetName={updateBackupStreetName}
              manualAddress={manualAddress}
              updateManualAddress={updateManualAddress}
              isConfirmationModalOpen={isConfirmationModalOpen}
              setIsConfirmationModalOpen={setIsConfirmationModalOpen}
            />
          )}
          {/******** PATH FOR ADDITIONAL BIN SERVICE REQUESTS ********/}
          {serviceRequest?.serviceRequest === FixedServiceRequestTypes.ADDITIONAL_BIN && (
            <StandardServicePath
              key={3}
              serviceRequest={serviceRequest}
              updateServiceRequest={updateServiceRequest}
              onSubmit={onSubmit}
              checkboxErrorMessage={checkboxErrorMessage}
              updateCheckboxErrorMessage={updateCheckboxErrorMessage}
              isLoading={isLoading}
              formErrorMessage={formErrorMessage}
              assessmentNoError={assessmentNoError}
              updateAssessmentNoError={updateAssessmentNoError}
              customerNoError={customerNoError}
              updateCustomerNoError={updateCustomerNoError}
              backupStreetNo={backupStreetNo}
              backupStreetName={backupStreetName}
              updateBackupStreetNo={updateBackupStreetNo}
              updateBackupPostCode={updateBackupPostCode}
              updateBackupStreetName={updateBackupStreetName}
              manualAddress={manualAddress}
              updateManualAddress={updateManualAddress}
              isConfirmationModalOpen={isConfirmationModalOpen}
              setIsConfirmationModalOpen={setIsConfirmationModalOpen}
            />
          )}
          {/******** PATH FOR GARDEN WASTE SERVICE REQUESTS ********/}
          {(serviceRequest?.serviceRequest === FixedServiceRequestTypes.GARDEN_WASTE_NEW ||
            serviceRequest?.serviceRequest === FixedServiceRequestTypes.GARDEN_WASTE_CHANGE ||
            serviceRequest?.serviceRequest === FixedServiceRequestTypes.CANCEL_GARDEN_WASTE) && (
            <StandardServicePath
              key={4}
              serviceRequest={serviceRequest}
              updateServiceRequest={updateServiceRequest}
              onSubmit={onSubmit}
              checkboxErrorMessage={checkboxErrorMessage}
              updateCheckboxErrorMessage={updateCheckboxErrorMessage}
              isLoading={isLoading}
              formErrorMessage={formErrorMessage}
              assessmentNoError={assessmentNoError}
              updateAssessmentNoError={updateAssessmentNoError}
              customerNoError={customerNoError}
              updateCustomerNoError={updateCustomerNoError}
              backupStreetNo={backupStreetNo}
              backupStreetName={backupStreetName}
              updateBackupStreetNo={updateBackupStreetNo}
              updateBackupPostCode={updateBackupPostCode}
              updateBackupStreetName={updateBackupStreetName}
              manualAddress={manualAddress}
              updateManualAddress={updateManualAddress}
              isConfirmationModalOpen={isConfirmationModalOpen}
              setIsConfirmationModalOpen={setIsConfirmationModalOpen}
            />
          )}
          {/******** PATH FOR NEW PROPERTY BIN SERVICE REQUESTS ********/}
          {serviceRequest?.serviceRequest === FixedServiceRequestTypes.NEW_PROPERTY_BIN && (
            <StandardServicePath
              key={5}
              serviceRequest={serviceRequest}
              updateServiceRequest={updateServiceRequest}
              onSubmit={onSubmit}
              checkboxErrorMessage={checkboxErrorMessage}
              updateCheckboxErrorMessage={updateCheckboxErrorMessage}
              isLoading={isLoading}
              formErrorMessage={formErrorMessage}
              assessmentNoError={assessmentNoError}
              updateAssessmentNoError={updateAssessmentNoError}
              customerNoError={customerNoError}
              updateCustomerNoError={updateCustomerNoError}
              backupStreetNo={backupStreetNo}
              backupStreetName={backupStreetName}
              updateBackupStreetNo={updateBackupStreetNo}
              updateBackupPostCode={updateBackupPostCode}
              updateBackupStreetName={updateBackupStreetName}
              manualAddress={manualAddress}
              updateManualAddress={updateManualAddress}
              isConfirmationModalOpen={isConfirmationModalOpen}
              setIsConfirmationModalOpen={setIsConfirmationModalOpen}
            />
          )}
          {/******** PATH FOR CHANGE BIN SIZE SERVICE REQUESTS ********/}
          {serviceRequest?.serviceRequest === FixedServiceRequestTypes.CHANGE_BIN_SIZES && (
            <StandardServicePath
              key={6}
              serviceRequest={serviceRequest}
              updateServiceRequest={updateServiceRequest}
              onSubmit={onSubmit}
              checkboxErrorMessage={checkboxErrorMessage}
              updateCheckboxErrorMessage={updateCheckboxErrorMessage}
              isLoading={isLoading}
              formErrorMessage={formErrorMessage}
              assessmentNoError={assessmentNoError}
              updateAssessmentNoError={updateAssessmentNoError}
              customerNoError={customerNoError}
              updateCustomerNoError={updateCustomerNoError}
              backupStreetNo={backupStreetNo}
              backupStreetName={backupStreetName}
              updateBackupStreetNo={updateBackupStreetNo}
              updateBackupPostCode={updateBackupPostCode}
              updateBackupStreetName={updateBackupStreetName}
              manualAddress={manualAddress}
              updateManualAddress={updateManualAddress}
              isConfirmationModalOpen={isConfirmationModalOpen}
              setIsConfirmationModalOpen={setIsConfirmationModalOpen}
            />
          )}
          {/******** PATH FOR MISSED COLLECTION SERVICE REQUESTS ********/}
          {serviceRequest?.serviceRequest === FixedServiceRequestTypes.MISSED_COLLECTION && (
            <StandardServicePath
              key={7}
              serviceRequest={serviceRequest}
              updateServiceRequest={updateServiceRequest}
              onSubmit={onSubmit}
              checkboxErrorMessage={checkboxErrorMessage}
              updateCheckboxErrorMessage={updateCheckboxErrorMessage}
              isLoading={isLoading}
              formErrorMessage={formErrorMessage}
              assessmentNoError={assessmentNoError}
              updateAssessmentNoError={updateAssessmentNoError}
              customerNoError={customerNoError}
              updateCustomerNoError={updateCustomerNoError}
              backupStreetNo={backupStreetNo}
              backupStreetName={backupStreetName}
              updateBackupStreetNo={updateBackupStreetNo}
              updateBackupPostCode={updateBackupPostCode}
              updateBackupStreetName={updateBackupStreetName}
              manualAddress={manualAddress}
              updateManualAddress={updateManualAddress}
              isConfirmationModalOpen={isConfirmationModalOpen}
              setIsConfirmationModalOpen={setIsConfirmationModalOpen}
            />
          )}
          {/******** PATH FOR ILLEGAL DUMPING SERVICE REQUESTS ********/}
          {serviceRequest?.serviceRequest === FixedServiceRequestTypes.ILLEGAL_DUMPING && (
            <IllegalDumpingPath
              key={8}
              serviceRequest={serviceRequest}
              updateServiceRequest={updateServiceRequest}
              onSubmit={onSubmit}
              isLoading={isLoading}
              formErrorMessage={formErrorMessage}
              updateOtherWasteDetails={updateOtherWasteDetails}
              wasteDescError={wasteDescError}
              updateWasteDescError={updateWasteDescError}
            />
          )}
          {/******** PATH FOR PUBLIC LITTER BIN SERVICE REQUESTS ********/}
          {serviceRequest?.serviceRequest === FixedServiceRequestTypes.PUBLIC_LITTER_BINS && (
            <PublicLitterBinsPath
              key={9}
              serviceRequest={serviceRequest}
              updateServiceRequest={updateServiceRequest}
              onSubmit={onSubmit}
              isLoading={isLoading}
              formErrorMessage={formErrorMessage}
            />
          )}
          {/******** PATH FOR LOOSE LITTER SERVICE REQUESTS ********/}
          {serviceRequest?.serviceRequest === FixedServiceRequestTypes.LOOSE_LITTER && (
            <LooseLitterPath
              key={10}
              serviceRequest={serviceRequest}
              updateServiceRequest={updateServiceRequest}
              onSubmit={onSubmit}
              isLoading={isLoading}
              formErrorMessage={formErrorMessage}
            />
          )}
        </AnimatePresence>
        <ErrorModal
          isLoading={isLoading}
          isModalOpen={isErrorModalOpen}
          setIsModalOpen={setIsErrorModalOpen}
          errorMessage={errorMessage}
          onSubmit={onSubmit}
        />
      </div>
    </div>
  );
}

export default Home;
